import type React from 'react'
import { createContext, useContext, useState, useRef, type ReactNode } from 'react'

import { LoginForm } from 'features/auth/LoginForm/LoginForm'
import { SignUpForm } from 'features/auth/SignUpForm/SignUpForm'
import { Button } from 'shadcdn/ui/button'

interface Props {
  children?: React.ReactNode
}

type AuthFormType = 'login' | 'signup' | 'activation'
type ShowLoginFormContext = (messsageToUser?: ReactNode, formType?: AuthFormType) => Promise<boolean>

const LoginContext = createContext<ShowLoginFormContext | undefined>(undefined)

export const AuthModalOverlay: React.FC<Props> = ({ children }) => {
  const [show, setShow] = useState(false)
  const [authForm, setAuthForm] = useState<AuthFormType>('login')
  const resolveRef = useRef<(value: boolean) => void>()
  const [messageToUser, setMessageToUser] = useState<ReactNode | null>(undefined)

  const showLoginForm: ShowLoginFormContext = (messageToUser, formType = 'login'): Promise<boolean> => {
    setAuthForm(formType)
    setShow(true)
    setMessageToUser(messageToUser)
    return new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }

  const onLoginFormSuccess = () => {
    setMessageToUser(undefined)
    setShow(false)
    resolveRef.current?.(true)
  }
  const onSignupFormSuccess = () => {
    setMessageToUser(undefined)
    setAuthForm('activation')
  }

  const onLoginFormCancel = () => {
    setMessageToUser(undefined)
    setShow(false)
    resolveRef.current?.(false)
  }

  return (
    <LoginContext.Provider value={showLoginForm}>
      {children}
      {show && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="relative bg-white rounded-lg p-6">
            <button
              type="button"
              className="absolute text-xl right-5 top-5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onLoginFormCancel}
            >
              &times;
            </button>
            {authForm === 'signup' ? (
              <SignUpForm
                onSuccess={onSignupFormSuccess}
                onClickSignIn={() => setAuthForm('login')}
                messageToUser={messageToUser}
              />
            ) : authForm === 'login' ? (
              <LoginForm
                onSuccess={onLoginFormSuccess}
                onClickSignUp={() => setAuthForm('signup')}
                messageToUser={messageToUser}
              />
            ) : authForm === 'activation' ? (
              <div className="w-80">
                <div className="text-center">
                  <h1 className="block text-2xl font-bold text-gray-800">Activation</h1>
                </div>
                <div className="mt-5">
                  <p>We've sent an activation email to your address.</p>
                  <p>Please check your inbox and click the link in the email to activate your account.</p>
                  <p>You'll need to activate your account before you can log in.</p>
                </div>
                <div className="mt-5 flex flex-col">
                  <Button variant="outline" onClick={() => setAuthForm('login')}>
                    Sign in
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </LoginContext.Provider>
  )
}

export const useShowAuthModal = () => {
  const context = useContext(LoginContext)
  if (!context) {
    throw new Error('useShowAuthModal must be used within a AuthModalOverlay')
  }
  return context
}
